import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Box,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  TableSortLabel,
  Button,
  Badge,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import _ from 'lodash';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import PendingItemsModal from '../Modal/PendingItemsModal';
import RefundItemsModal from '../Modal/RefundItemsModal';

export default function BudgetTable({
  budget,
  handleEditBudget,
  handleDeleteBudget,
  onEditExpense,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  const [expenseSortBy, setExpenseSortBy] = useState({}); // { [budgetId]: 'date' | 'price' }
  const [pendingModalId, setPendingModalId] = useState(null);
  const [refundModalId, setRefundModalId] = useState(null);

  const pendingModalBudget = _.find(budget, (b) => b.id === pendingModalId);
  const refundModalBudget = _.find(budget, (b) => b.id === refundModalId);

  const openPendingModal = (budgetId) => setPendingModalId(budgetId);
  const closePendingModal = () => setPendingModalId(null);

  const openRefundModal = (budgetId) => setRefundModalId(budgetId);
  const closeRefundModal = () => setRefundModalId(null);

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allExpanded =
    !_.isEmpty(budget) &&
    budget.filter((e) => !_.isEmpty(e?.expenses)).every((e) => !!expandedRows[e.id]);

  const toggleAllRows = () => {
    if (allExpanded) {
      setExpandedRows({});
    } else {
      const all = {};
      (budget || []).forEach((e) => { if (!_.isEmpty(e?.expenses)) all[e.id] = true; });
      setExpandedRows(all);
    }
  };

  const toggleExpenseSort = (id, field) => {
    setExpenseSortBy((prev) => ({ ...prev, [id]: prev[id] === field ? 'date' : field }));
  };

  const getSortedExpenses = (expenses, budgetId) => {
    const sortField = expenseSortBy[budgetId] || 'date';
    if (sortField === 'price') {
      return _.orderBy(expenses, ['price'], ['desc']);
    }
    return _.orderBy(expenses, [(e) => dayjs(e?.date).valueOf()], ['desc']);
  };

  const expenseStatusIcon = (status) => {
    if (status === 'SUCCESS')
      return (
        <Tooltip title="สำเร็จ">
          <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'APPROVE')
      return (
        <Tooltip title="อนุมัติแล้ว">
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'warning.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'REJECT')
      return (
        <Tooltip title="ถูกปฏิเสธ">
          <CancelIcon fontSize="small" sx={{ color: 'error.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    if (status === 'PREAPPROVE')
      return (
        <Tooltip title="รออนุมัติเบื้องต้น">
          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'info.main', verticalAlign: 'middle' }} />
        </Tooltip>
      );
    return (
      <Tooltip title="รอดำเนินการ">
        <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled', verticalAlign: 'middle' }} />
      </Tooltip>
    );
  };
  const renderCost = (data) => {
    const fil = _?.filter(data?.expenses, (each) => each?.status === 'SUCCESS');
    const cost1 = _?.sumBy(fil, (e) => e?.price);
    return cost1;
  };

  const renderCostApprove = (data) => {
    const fil = _?.filter(
      data?.expenses,
      (each) => each?.status !== 'SUCCESS' && each?.status !== 'REJECT'
    );
    const cost1 = _?.sumBy(fil, (e) => e?.price);
    return cost1;
  };

  const renderPendingUnpaid = (data) => {
    const fil = _?.filter(data?.pending_items, (each) => !each?.paid);
    return _?.sumBy(fil, (i) => i?.amount) || 0;
  };

  // ค่ามัดจำ/ค่าประกันที่ติดมากับ expense (นับเป็นต้นทุนไปแล้ว) แต่รอได้รับเงินคืน — ไม่ใช่ต้นทุนจริงของบริษัท จึงหักออกจาก Grand Total
  const renderRefundPending = (data) => {
    const fil = _?.filter(data?.refund_items, (each) => !each?.refunded);
    return _?.sumBy(fil, (i) => i?.amount) || 0;
  };

  const renderGrandTotal = (data) => {
    return (renderCost(data) || 0) + (renderCostApprove(data) || 0) + (renderPendingUnpaid(data) || 0) - (renderRefundPending(data) || 0);
  };

  const renderPercen = (data) => {
    const cost1 = renderGrandTotal(data);

    const per = (cost1 / (data?.cost === 0 ? 1 : data?.cost)) * 100;

    return per; // Actual percentage value
  };

  const renderTotal = (data) => {
    return data?.cost - renderGrandTotal(data);
  };

  const normalizePercentage = (percentage) => {
    return Math.min(percentage, 100); // Normalize to fit within 0–100
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={toggleAllRows}
          startIcon={allExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          disabled={_.isEmpty(budget) || budget.every((e) => _.isEmpty(e?.expenses))}
        >
          {allExpanded ? 'ปิดทั้งหมด' : 'ดูค่าใช้จ่ายทั้งหมด'}
        </Button>
      </Box>
      <Paper>
        <TableContainer component={Paper}>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': { borderRight: '1px solid', borderRightColor: 'rgba(224, 224, 224, 1)' },
              '& thead .MuiTableCell-root': { borderRightColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            <TableHead>
              <TableRow className={'bg-gray-600'}>
                <TableCell sx={{ minWidth: 32 }} />
                <TableCell sx={{ minWidth: 100 }}>
                  <div className="text-white">No</div>
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <div className="text-white">Order</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <div className="text-white">Budget</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <div className="text-white">Paid</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 90 }}>
                  <div className="text-white">Expected</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <div className="text-white">AP Pending</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <div className="text-white">รอเงินคืน</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>
                  <div className="text-white">Grand Total</div>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  <div className="text-white">Percent</div>
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  <div className="text-white">Remaining</div>
                </TableCell>
                <TableCell
                  sx={{ minWidth: 100, position: 'sticky', right: 0, zIndex: 3 }}
                  className="bg-gray-600"
                ></TableCell>
              </TableRow>
              <TableRow className={'bg-gray-100'} sx={{ '& .MuiTableCell-root': { borderRight: '1px solid', borderRightColor: 'rgba(224, 224, 224, 1)' } }}>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell align="right">
                  <span style={{ fontWeight: 600 }}>
                    {_.sumBy(budget, (e) => e?.cost || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <span style={{ fontWeight: 600 }}>
                    {_.sumBy(budget, (e) => renderCost(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <span style={{ fontWeight: 600 }}>
                    {_.sumBy(budget, (e) => renderCostApprove(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <span style={{ fontWeight: 600, color: '#ed6c02' }}>
                    {_.sumBy(budget, (e) => renderPendingUnpaid(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </span>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const refundTotal = _.sumBy(budget, (e) => renderRefundPending(e) || 0);
                    return (
                      <span style={{ fontWeight: 600, color: refundTotal > 0 ? '#d32f2f' : 'inherit' }}>
                        {refundTotal > 0 ? '-' : ''}
                        {refundTotal.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  <span style={{ fontWeight: 600 }}>
                    {_.sumBy(budget, (e) => renderGrandTotal(e) || 0)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </span>
                </TableCell>
                <TableCell align="center">
                  <span style={{ fontWeight: 600 }}>
                    {(
                      (_.sumBy(budget, (e) => renderGrandTotal(e) || 0) /
                        (_.sumBy(budget, (e) => e?.cost || 0) || 1)) *
                      100
                    ).toFixed(2)}
                    %
                  </span>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const total = _.sumBy(budget, (e) => renderTotal(e) || 0);
                    return (
                      <span style={{ fontWeight: 600, color: total < 0 ? '#d32f2f' : 'inherit' }}>
                        {total.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell
                  sx={{ position: 'sticky', right: 0, zIndex: 3 }}
                  className="bg-gray-100"
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {_.isEmpty(budget) ? (
                <TableRow>
                  <TableCell colSpan={12}>
                    <div className="flex justify-center">No items</div>
                  </TableCell>
                </TableRow>
              ) : (
                _?.map(budget, (e) => (
                  <React.Fragment key={e.id}>
                    <TableRow
                      hover
                      sx={{ cursor: !_.isEmpty(e?.expenses) ? 'pointer' : 'default' }}
                      onClick={() => !_.isEmpty(e?.expenses) && toggleRow(e.id)}
                    >
                      <TableCell padding="checkbox">
                        {!_.isEmpty(e?.expenses) && (
                          <Tooltip title={expandedRows[e.id] ? 'ซ่อนค่าใช้จ่าย' : 'แสดงค่าใช้จ่าย'}>
                            <IconButton size="small">
                              {expandedRows[e.id]
                                ? <KeyboardArrowUpIcon fontSize="small" />
                                : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {e?.prefix}
                        {e?.budget_number}
                      </TableCell>
                      <TableCell>{e?.name}</TableCell>
                      <TableCell align="right">
                        {e?.cost?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="right">
                        {renderCost(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="right">
                        {renderCostApprove(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: renderPendingUnpaid(e) > 0 ? '#ed6c02' : 'inherit' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={(ev) => { ev.stopPropagation(); openPendingModal(e.id); }}
                          >
                            {renderPendingUnpaid(e)
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                          </span>
                          <Tooltip title="รายการเงินที่รอจ่าย">
                            <IconButton
                              aria-label="pending payment"
                              size="small"
                              color="warning"
                              onClick={(ev) => { ev.stopPropagation(); openPendingModal(e.id); }}
                            >
                              <Badge badgeContent={_.filter(e?.pending_items, (i) => !i?.paid).length} color="warning">
                                <HourglassEmptyIcon fontSize="inherit" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: renderRefundPending(e) > 0 ? '#d32f2f' : 'inherit' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={(ev) => { ev.stopPropagation(); openRefundModal(e.id); }}
                          >
                            {renderRefundPending(e) > 0 ? '-' : ''}
                            {renderRefundPending(e)
                              .toFixed(2)
                              .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                          </span>
                          <Tooltip title="รายการเงินที่รอรับคืน">
                            <IconButton
                              aria-label="refund pending"
                              size="small"
                              color="info"
                              onClick={(ev) => { ev.stopPropagation(); openRefundModal(e.id); }}
                            >
                              <Badge badgeContent={_.filter(e?.refund_items, (i) => !i?.refunded).length} color="info">
                                <AssignmentReturnIcon fontSize="inherit" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {renderGrandTotal(e)
                          .toFixed(2)
                          .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell align="center">
                        <Box className="relative w-full">
                          <LinearProgress
                            variant="determinate"
                            value={normalizePercentage(renderPercen(e))}
                            sx={{
                              height: 20,
                              borderRadius: 5,
                              backgroundColor: renderPercen(e) < 0 ? '#f44336' : '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor:
                                  renderPercen(e) > 80 || renderPercen(e) < 0
                                    ? '#f44336'
                                    : renderPercen(e) < 50
                                    ? '#4caf50'
                                    : '#ffb300',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            className="absolute inset-0 flex items-center justify-center font-bold"
                          >
                            {renderPercen(e)?.toFixed(2)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ color: renderTotal(e) < 0 ? '#d32f2f' : 'inherit' }}>
                        {renderTotal(e)
                          ?.toFixed(2)
                          ?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                      </TableCell>
                      <TableCell
                        onClick={(ev) => ev.stopPropagation()}
                        sx={{
                          position: 'sticky',
                          right: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <IconButton
                          aria-label="edit"
                          size="small"
                          onClick={() => handleEditBudget(e)}
                        >
                          <ModeEditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          onClick={() => handleDeleteBudget(e)}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded expense rows */}
                    <TableRow>
                      <TableCell colSpan={12} sx={{ p: 0, border: 0, width: 0 }}>
                        <Collapse in={!!expandedRows[e.id]} timeout="auto" unmountOnExit>
                          <Box sx={{ mx: 2, my: 1, width: 0, minWidth: '100%', overflowX: 'auto' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                              ค่าใช้จ่ายใน {e?.prefix}{e?.budget_number} — {e?.name}
                            </Typography>
                            <Table
                              size="small"
                              sx={{
                                tableLayout: 'fixed',
                                width: '100%',
                                '& .MuiTableCell-root': { borderRight: '1px solid', borderRightColor: 'rgba(224, 224, 224, 1)' },
                              }}
                            >
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell sx={{ width: 100 }}>
                                    <TableSortLabel
                                      active={!expenseSortBy[e.id] || expenseSortBy[e.id] === 'date'}
                                      direction="desc"
                                      onClick={() => toggleExpenseSort(e.id, 'date')}
                                    >
                                      <Typography variant="caption" fontWeight={600}>วันที่</Typography>
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell sx={{ width: 110 }}><Typography variant="caption" fontWeight={600}>รหัส</Typography></TableCell>
                                  <TableCell><Typography variant="caption" fontWeight={600}>รายการ</Typography></TableCell>
                                  <TableCell align="right" sx={{ width: 110 }}>
                                    <TableSortLabel
                                      active={expenseSortBy[e.id] === 'price'}
                                      direction="desc"
                                      onClick={() => toggleExpenseSort(e.id, 'price')}
                                    >
                                      <Typography variant="caption" fontWeight={600}>จำนวนเงิน</Typography>
                                    </TableSortLabel>
                                  </TableCell>
                                  <TableCell align="center" sx={{ width: 70 }}><Typography variant="caption" fontWeight={600}>สถานะ</Typography></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {_.isEmpty(e?.expenses) ? (
                                  <TableRow>
                                    <TableCell colSpan={5} align="center">
                                      <Typography variant="caption" color="text.secondary">ไม่มีค่าใช้จ่าย</Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  <>
                                    {getSortedExpenses(e?.expenses, e.id).map((exp) => (
                                      <TableRow
                                        key={exp?._id || exp?.id}
                                        hover
                                        onDoubleClick={() => onEditExpense?.(exp?._id || exp?.id)}
                                        sx={{ cursor: onEditExpense ? 'pointer' : 'default' }}
                                      >
                                        <TableCell sx={{ width: 100 }}>
                                          <Typography variant="caption">
                                            {exp?.date ? dayjs(exp.date).format('DD/MM/YYYY') : '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: 110 }}>
                                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                            {exp?.code || '-'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 0, overflow: 'hidden' }}>
                                          <Tooltip title={exp?.name || ''}>
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            >
                                              {exp?.name || '-'}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: 110 }}>
                                          <Typography variant="caption">
                                            {exp?.price?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,') || '0.00'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: 70 }}>
                                          {expenseStatusIcon(exp?.status)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {/* Summary row */}
                                    <TableRow sx={{ backgroundColor: '#fafafa', '& .MuiTableCell-root': { borderRight: '1px solid', borderRightColor: 'rgba(224, 224, 224, 1)' } }}>
                                      <TableCell />
                                      <TableCell />
                                      <TableCell>
                                        <Typography variant="caption" fontWeight={600}>รวม</Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="caption" fontWeight={600}>
                                          {_.sumBy(e?.expenses, (exp) => exp?.status === 'REJECT' ? 0 : (exp?.price || 0))
                                            .toFixed(2)
                                            .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                        </Typography>
                                      </TableCell>
                                      <TableCell />
                                    </TableRow>
                                  </>
                                )}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PendingItemsModal
        open={!!pendingModalId}
        budget={pendingModalBudget}
        onClose={closePendingModal}
      />

      <RefundItemsModal
        open={!!refundModalId}
        budget={refundModalBudget}
        onClose={closeRefundModal}
      />
    </div>
  );
}
